import { Icon, Card, Spinner } from '@scale/scaleui';
import SAlert from 'react-s-alert';
import { Event, logEvent } from 'react-scale/lib/events';
import { MultimodalPromptSuggestionEventType } from '@scale/shared/types/events/MultimodalPromptSuggestionEvent';
import { useContext } from 'react';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';

interface MultimodalPromptSuggestionsCardProps {
  imageUrl: string;
  isFetchingPromptSuggestions: boolean;
  promptSuggestions?: string[];
  appendPromptText?: (textToAppend: string) => void;
}

const MultimodalPromptSuggestionsCard = ({
  imageUrl,
  isFetchingPromptSuggestions,
  promptSuggestions,
  appendPromptText,
}: MultimodalPromptSuggestionsCardProps) => {
  const { projectId, assignmentId } = useContext(ChatTaskContext);

  return (
    <div
      className={
        'text-left transition-all border border-neutral-200 rounded-lg shadow-sm h-fit scaleui bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400 overflow-hidden'
      }
    >
      <div className="px-4 py-3 gap-2">
        <p className={'text-sm font-medium text-neutral-800'}>Prompt Suggestions</p>
        {isFetchingPromptSuggestions && (
          <div className="flex flex-row gap-2 items-center mt-2">
            <p className={'text-sm text-neutral-800'}>Fetching prompt suggestions</p>
            <Spinner size="xs" />
          </div>
        )}
        {!isFetchingPromptSuggestions && promptSuggestions && (
          <div className="flex flex-col gap-2 pt-2">
            <span className={'text-sm text-neutral-800 italic'}>
              Use or modify any of the following suggestions. Click on a suggestion to insert it
              into the prompt field.
            </span>
            {promptSuggestions.map((suggestedPrompt: string, index: number) => (
              <Card
                key={index}
                title={
                  <div className="flex flex-row items-center">
                    <div className="flex-grow text-sm text-neutral-800 font-normal">
                      {suggestedPrompt}
                    </div>
                    <div className="flex-shrink-0">
                      <Icon icon="copy" size="sm" variant="neutral" />
                    </div>
                  </div>
                }
                className="w-full border border-neutral-200 hover:shadow-lg"
                onClick={async () => {
                  if (!isFetchingPromptSuggestions) {
                    const promptSuggestionToCopy = promptSuggestions[index];
                    navigator.clipboard
                      .writeText(promptSuggestionToCopy)
                      .then(() => {
                        SAlert.info('Prompt suggestion copied to clipboard');
                        return;
                      })
                      .catch(_err => {
                        SAlert.error('Failed to copy prompt suggestion');
                      });
                    if (appendPromptText) {
                      appendPromptText(promptSuggestionToCopy);
                    }
                    await logEvent(Event.MultimodalPromptSuggestionEvent, {
                      type: MultimodalPromptSuggestionEventType.PROMPT_SUGGESTION_COPY,
                      projectId,
                      assignmentId,
                      imageUrl,
                      promptSuggestions,
                      copiedPromptSuggestion: promptSuggestionToCopy,
                    });
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultimodalPromptSuggestionsCard;
